import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../services/api';
import { AnalyticsShell, StatCard } from '../components/analytics/AnalyticsShell';
import { Stagger, fadeUp } from '../components/analytics/anim';
import { ago, dur } from '../components/analytics/format';

export const Route = createFileRoute('/analytics/actors/')({
  component: ActorList,
});

interface ActorRow {
  actor: string;
  /** Present only for first-party learners; provider actors stay opaque. */
  learnerName?: string | null;
  learnerEmail?: string | null;
  learnerStatus?: string | null;
  views: number;
  videos: number;
  completes: number;
  avgPercentViewed: number;
  bestPercentViewed: number;
  watchMs: number;
  firstSeenAt: string;
  lastSeenAt: string;
  providers: string[];
  interactions: number;
  graded: number;
  correct: number;
}

function ActorList() {
  const [actors, setActors] = useState<ActorRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [provider, setProvider] = useState<string>('');

  useEffect(() => {
    api
      .get('/analytics/dashboard/actors')
      .then((r) => setActors(r.data.actors ?? []))
      .catch((e) =>
        setError(
          e?.response?.status === 403
            ? 'Admin access required — this dashboard is restricted to administrators.'
            : e?.response?.data?.error ?? e?.message ?? 'Failed to load',
        ),
      );
  }, []);

  const providers = useMemo(
    () => [...new Set((actors ?? []).flatMap((a) => a.providers ?? []))].sort(),
    [actors],
  );

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (actors ?? []).filter(
      (a) =>
        (!needle ||
          a.actor.toLowerCase().includes(needle) ||
          (a.learnerEmail ?? '').toLowerCase().includes(needle) ||
          (a.learnerName ?? '').toLowerCase().includes(needle)) &&
        (!provider || (a.providers ?? []).includes(provider)),
    );
  }, [actors, q, provider]);

  const totals = rows.reduce(
    (acc, a) => ({
      views: acc.views + a.views,
      watch: acc.watch + (a.watchMs ?? 0),
      interactions: acc.interactions + a.interactions,
    }),
    { views: 0, watch: 0, interactions: 0 },
  );

  return (
    <AnalyticsShell crumb="Learners">
      <div className="mb-7">
        <h1 className="text-2xl font-semibold tracking-tight">Learners</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every identified learner, keyed by the opaque <code className="text-xs">actor</code> their platform passes
          on the launch URL. Each watch is recorded separately — a learner who watches the same video twice shows two
          distinct views.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {!actors && !error && <div className="text-sm text-muted-foreground">Loading…</div>}

      {actors && actors.length === 0 && (
        <div className="rounded-xl border border-border/60 bg-card py-16 text-center">
          <div className="text-sm font-medium">No identified learners yet</div>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Learners appear here once a video is opened with an <code className="text-xs">?actor=</code> on the URL.
            Views without one stay anonymous and are counted on the video pages.
          </p>
        </div>
      )}

      {actors && actors.length > 0 && (
        <>
          <Stagger className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label="Learners" value={rows.length} accent />
            <StatCard label="Views" value={totals.views.toLocaleString()} />
            <StatCard label="Watch time" value={dur(totals.watch)} />
            <StatCard label="Interactions" value={totals.interactions.toLocaleString()} />
          </Stagger>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, or actor id…"
              className="h-9 w-72 rounded-lg border border-border/60 bg-card px-3 text-sm outline-none focus:border-primary/60"
            />
            {providers.length > 0 && (
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="h-9 rounded-lg border border-border/60 bg-card px-3 text-sm outline-none focus:border-primary/60"
              >
                <option value="">All providers</option>
                {providers.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            )}
            <span className="text-xs text-muted-foreground">
              {rows.length} of {actors.length}
            </span>
          </div>

          <Stagger className="overflow-hidden rounded-xl border border-border/60 bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-5 py-2.5 text-left font-medium">Actor</th>
                    <th className="px-5 py-2.5 text-left font-medium">Provider</th>
                    <th className="px-5 py-2.5 text-right font-medium">Videos</th>
                    <th className="px-5 py-2.5 text-right font-medium">Views</th>
                    <th className="px-5 py-2.5 text-right font-medium">Watch</th>
                    <th className="px-5 py-2.5 text-right font-medium">Best %</th>
                    <th className="px-5 py-2.5 text-right font-medium">Correct</th>
                    <th className="px-5 py-2.5 text-right font-medium">Last seen</th>
                    <th className="px-5 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <motion.tr
                      key={a.actor}
                      variants={fadeUp}
                      className="border-b border-border/40 last:border-0 hover:bg-muted/40"
                    >
                      <td className="max-w-[22rem] px-5 py-2.5">
                        <Link
                          to="/analytics/actors/$actorId"
                          params={{ actorId: a.actor }}
                          className="text-primary hover:underline"
                        >
                          {/* Known learners read as people; provider-issued
                              actors have no identity on file and stay opaque. */}
                          {a.learnerEmail ? (
                            <span className="block truncate text-xs">
                              {a.learnerName || a.learnerEmail}
                            </span>
                          ) : (
                            <span className="block truncate font-mono text-xs">{a.actor}</span>
                          )}
                        </Link>
                        {a.learnerEmail && a.learnerName && (
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {a.learnerEmail}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-xs text-muted-foreground">
                        {(a.providers ?? []).join(', ') || '—'}
                      </td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{a.videos}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{a.views}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{dur(a.watchMs)}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{a.bestPercentViewed}%</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">
                        {a.graded > 0 ? `${a.correct}/${a.graded}` : '—'}
                      </td>
                      <td className="whitespace-nowrap px-5 py-2.5 text-right text-xs text-muted-foreground">
                        {ago(a.lastSeenAt)}
                      </td>
                      <td className="px-5 py-2.5 text-right text-xs text-primary">
                        <Link to="/analytics/actors/$actorId" params={{ actorId: a.actor }}>
                          Timeline →
                        </Link>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Stagger>
        </>
      )}
    </AnalyticsShell>
  );
}
